<script>
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import SigningIdentityDialog from './SigningIdentityDialog.svelte';
  import GatewaySigningModal from './GatewaySigningModal.svelte';
  import { getCurrentRequirements, openAgreementFile, resumeSigning, restartLostSigning, restartPendingSigning, pendingRestartConfirmation, pendingRestartUnconfirmed } from './agreementApi.js';

  let requirements = [];
  let visible = false;
  let fileError = '';
  let selected = null;
  let signingTrigger = null;
  let gatewaySession = null;
  let restarting = false;

  const isOutstanding = item => item.status !== 'withdrawn' && item.status !== 'completed_stored';
  // The server is authoritative: only an exact requirement explicitly marked
  // initiable may expose the identity/signing action here.
  const isInitiable = item => isOutstanding(item) && item.can_initiate === true;

  async function load() {
    try {
      requirements = (await getCurrentRequirements()).filter(isOutstanding);
      visible = requirements.length > 0;
    } catch (_) {
      // Fail closed if authentication, authorization, feature availability, or
      // transport state changes after a previously successful load.
      requirements = [];
      selected = null;
      fileError = '';
      visible = false;
    }
  }

  onMount(load);

  $: initiableRequirement = requirements.find(isInitiable) || null;
  $: resumableRequirement = requirements.find(item => item.can_resume === true) || null;
  $: lostRequirement = requirements.find(item => item.can_restart_local === true) || null;

  async function preview() {
    fileError = '';
    try { await openAgreementFile(requirements[0].public_id || requirements[0].id, 'source'); }
    catch (_) { fileError = 'Sutarties nepavyko atidaryti.'; }
  }

  function startSigning(event) {
    if (!initiableRequirement) return;
    signingTrigger = event.currentTarget;
    selected = initiableRequirement;
  }

  async function resume(event) {
    if (!resumableRequirement) return;
    signingTrigger = event.currentTarget;
    try {
      const result = await resumeSigning(resumableRequirement.public_id || resumableRequirement.id);
      if (result?.mode !== 'gateway_iframe' || typeof result?.launch_url !== 'string') throw new Error('invalid_resume');
      gatewaySession = { requirement: resumableRequirement, launchUrl: result.launch_url, returnFocus: signingTrigger };
    } catch (_) { fileError = 'Pasirašymo tęsti nepavyko. Atnaujinkite būseną Mano profilyje.'; }
  }

  async function submitted(event) {
    if (event.detail.mode === 'gateway_iframe') {
      gatewaySession = { requirement: selected, launchUrl: event.detail.result.launch_url, returnFocus: signingTrigger };
      selected = null;
      await load();
      return;
    }
    selected = null;
    await load();
  }

  async function restartLocal() {
    if (!lostRequirement) return;
    try { await restartLostSigning(lostRequirement.public_id || lostRequirement.id); await load(); }
    catch (_) { fileError = 'Vietinės bandomosios būsenos atkurti nepavyko.'; }
  }

  async function restartPending(event) {
    const item = resumableRequirement;
    if (!item?.can_restart_pending || restarting || !window.confirm(pendingRestartConfirmation)) return;
    restarting = true;
    signingTrigger = event.currentTarget;
    try {
      const current = await restartPendingSigning(item.public_id || item.id, item.pending_restart_transaction_id);
      await load();
      if (current?.status !== 'assigned' || current?.can_initiate !== true) throw new Error('restart_not_ready');
      selected = current;
    } catch (error) {
      await load();
      fileError = error?.code === 'signing_restart_unconfirmed' ? pendingRestartUnconfirmed : 'Naujo pasirašymo pradėti nepavyko. Atnaujinkite būseną Mano profilyje.';
    } finally { restarting = false; }
  }

  function dismiss() {
    if (restarting) return;
    selected = null;
    visible = false;
  }
</script>

{#if visible}
  <aside class="notice" role="status" aria-live="polite">
    <button type="button" class="close" aria-label="Uždaryti pranešimą šiam apsilankymui" on:click={dismiss}>×</button>
    <strong>{requirements.length === 1 ? 'Turite nepasirašytą sutartį.' : `Turite nepasirašytų sutarčių: ${requirements.length}.`}</strong>
    <p>Peržiūrėti ir pasirašyti galite Mano profilyje.</p>
    {#if fileError}<p class="error">{fileError}</p>{/if}
    {#if requirements[0]?.family}<p>Šeimos sutartis: {requirements[0].family.name}. Pakanka vieno parašo visai šeimai.</p>{/if}
    <div class="actions">
      {#if lostRequirement}<button type="button" class="primary" on:click={restartLocal}>Leisti naują bandomąjį bandymą</button>{/if}
      {#if resumableRequirement}<button type="button" class="primary" disabled={restarting} on:click={resume}>Tęsti pasirašymą</button>
        {#if resumableRequirement.can_restart_pending === true}<button type="button" disabled={restarting} aria-busy={restarting} on:click={restartPending}>Pradėti iš naujo</button>{/if}
      {:else if initiableRequirement}<button type="button" class="primary" on:click={startSigning}>Pasirašyti Dokobit</button>{/if}
      <button type="button" on:click={preview}>Peržiūrėti</button>
      <button type="button" on:click={() => push('/account')}>Eiti į Mano profilį</button>
    </div>
  </aside>
{/if}

<SigningIdentityDialog requirement={selected} open={!!selected} returnFocus={signingTrigger} on:close={() => selected = null} on:submitted={submitted}/>
<GatewaySigningModal requirement={gatewaySession?.requirement} launchUrl={gatewaySession?.launchUrl || ''} open={!!gatewaySession} returnFocus={gatewaySession?.returnFocus} on:close={() => gatewaySession = null} on:refresh={load} on:complete={async () => { gatewaySession = null; await load(); }}/>

<style>
  .notice{position:relative;box-sizing:border-box;width:100%;min-width:0;border:1px solid #f59e0b;background:#fef3c7;color:#854d0e;border-radius:8px;padding:16px 42px 16px 16px;box-shadow:0 .5px 2px rgba(96,97,112,.16)}.notice p{margin:7px 0}.close{position:absolute;right:6px;top:5px;width:36px;height:36px;border:0;background:transparent;color:#854d0e;font-size:24px;cursor:pointer}.actions{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}.actions button{min-height:44px;max-width:100%;border:1px solid #14695b;border-radius:8px;background:white;color:#14695b;padding:8px 12px;font-weight:600;cursor:pointer}.actions .primary{background:#fab45a;border-color:#333;color:#111}.actions button:focus,.close:focus{outline:2px solid #14695b;outline-offset:2px}.error{color:#b91c1c}@media(max-width:480px){.actions{flex-direction:column}.actions button{width:100%}}
</style>
